import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Table, Button, Alert, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import {
  FireIcon, PlayIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { companyAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface ProductionIngredient {
  id: number;
  ingredient: number | null;
  ingredient_name: string;
  quantity: string;
  unit: string;
  is_starter: boolean;
}

interface Production {
  id: number;
  name: string;
  status: 'waiting' | 'started' | 'completed' | 'cancelled';
  status_display: string;
  production_date: string;
  notes: string;
  inventory_deducted: boolean;
  responsible_user: number | null;
  responsible_user_name: string | null;
  ingredients: ProductionIngredient[];
  created_at: string;
}

function fmtQty(quantity: number | string, unit: string): string {
  const q = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  if (isNaN(q)) return `${quantity} ${unit}`;
  const u = (unit || '').toLowerCase().trim();
  if (u === 'g' && q >= 1000) return `${parseFloat((q / 1000).toFixed(3).replace(/\.?0+$/, ''))} kg`;
  if (u === 'ml' && q >= 1000) return `${parseFloat((q / 1000).toFixed(3).replace(/\.?0+$/, ''))} l`;
  const display = q % 1 === 0 ? q.toLocaleString() : parseFloat(q.toFixed(3)).toLocaleString();
  return `${display} ${unit}`;
}

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

const statusConfig: Record<string, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
  waiting:   { color: '#92400e', bg: '#fef3c7', icon: <ClockIcon style={{ width: 12, height: 12 }} />, label: 'Waiting' },
  started:   { color: '#1d4ed8', bg: '#dbeafe', icon: <PlayIcon style={{ width: 12, height: 12 }} />, label: 'Started' },
  completed: { color: '#065f46', bg: '#d1fae5', icon: <CheckCircleIcon style={{ width: 12, height: 12 }} />, label: 'Completed' },
  cancelled: { color: '#7f1d1d', bg: '#fee2e2', icon: <XCircleIcon style={{ width: 12, height: 12 }} />, label: 'Cancelled' },
};

const nextStatuses: Record<string, { value: string; label: string; variant: string }[]> = {
  waiting:   [{ value: 'started', label: 'Mark Started', variant: 'outline-primary' }, { value: 'cancelled', label: 'Cancel', variant: 'outline-danger' }],
  started:   [{ value: 'completed', label: 'Mark Completed', variant: 'outline-success' }, { value: 'cancelled', label: 'Cancel', variant: 'outline-danger' }],
  completed: [],
  cancelled: [],
};

export default function CompanyProductions() {
  const { user } = useAuth();
  const isOwner = !user?.is_company_admin || user?.is_company_owner;
  const [productions, setProductions] = useState<Production[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelModal, setCancelModal] = useState<{ show: boolean; production: Production | null }>({ show: false, production: null });
  const [staffMembers, setStaffMembers] = useState<{ id: number; username: string; first_name: string; last_name: string }[]>([]);
  const [assigningId, setAssigningId] = useState<number | null>(null);

  const fetchProductions = useCallback(() => {
    setLoading(true);
    companyAPI.getProductions()
      .then(r => setProductions(r.data))
      .catch(() => setError('Failed to load productions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchProductions();
    companyAPI.getStaffList().then(r => setStaffMembers(r.data)).catch(() => {});
  }, [fetchProductions]);

  const updateStatus = async (id: number, newStatus: string) => {
    try {
      const res = await companyAPI.updateProductionStatus(id, { status: newStatus });
      setProductions(prev => prev.map(p => p.id === id ? res.data : p));
    } catch {
      setError('Failed to update status.');
    }
  };

  const updateResponsible = async (id: number, userId: number | '') => {
    setAssigningId(id);
    try {
      const res = await companyAPI.updateProductionStatus(id, { responsible_user_id: userId || null });
      setProductions(prev => prev.map(p => p.id === id ? res.data : p));
    } catch {
      setError('Failed to assign staff member.');
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        <div className="d-flex align-items-center gap-2 mb-4">
          <div style={{ backgroundColor: '#fef3c7', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FireIcon style={{ width: '22px', height: '22px', color: '#d97706' }} />
          </div>
          <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Production</h4>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3" style={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>
        ) : productions.length === 0 ? (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body className="text-center py-5 text-muted">
              <FireIcon style={{ width: '40px', height: '40px', color: '#d1d5db', marginBottom: '12px' }} />
              <div style={{ fontSize: '0.9rem' }}>No production runs yet.</div>
              <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>Create one from the Batch Optimizer.</div>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ ...thStyle, width: '60px' }}>#</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Responsible</th>
                    <th style={thStyle}>Ingredients</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productions.map((prod, idx) => {
                    const cfg = statusConfig[prod.status] || statusConfig.waiting;
                    const actions = nextStatuses[prod.status] || [];
                    return (
                      <tr key={prod.id}>
                        <td style={{ padding: '12px 16px', fontSize: '0.78rem', fontWeight: 700, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          #{productions.length - idx}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.85rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                          {prod.production_date}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.88rem', fontWeight: 500, maxWidth: 220 }}>
                          {prod.name || `Production #${prod.id}`}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                            color: cfg.color, backgroundColor: cfg.bg,
                          }}>
                            {cfg.icon} {cfg.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.82rem', color: '#374151', minWidth: 160 }}>
                          {assigningId === prod.id
                            ? <Spinner animation="border" size="sm" variant="secondary" />
                            : (
                              <Form.Select
                                size="sm"
                                value={prod.responsible_user ?? ''}
                                disabled={!isOwner || prod.status === 'completed' || prod.status === 'cancelled'}
                                onChange={e => updateResponsible(prod.id, e.target.value ? Number(e.target.value) : '')}
                                style={{ fontSize: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', maxWidth: 180 }}
                              >
                                <option value="">— Unassigned —</option>
                                {staffMembers.map(s => (
                                  <option key={s.id} value={s.id}>
                                    {`${s.first_name} ${s.last_name}`.trim() || s.username}
                                  </option>
                                ))}
                              </Form.Select>
                            )
                          }
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: '#6b7280', maxWidth: 260 }}>
                          {prod.ingredients.slice(0, 4).map((ing, i) => (
                            <span key={i}>
                              {i > 0 && ', '}{ing.ingredient_name} {fmtQty(ing.quantity, ing.unit)}
                            </span>
                          ))}
                          {prod.ingredients.length > 4 && <span> +{prod.ingredients.length - 4} more</span>}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {actions.map(act => (
                            <Button
                              key={act.value}
                              variant={act.variant as any}
                              size="sm"
                              className="ms-1"
                              style={{ borderRadius: '7px', fontSize: '0.78rem' }}
                              onClick={() => {
                                if (act.value === 'cancelled') {
                                  setCancelModal({ show: true, production: prod });
                                } else {
                                  updateStatus(prod.id, act.value);
                                }
                              }}
                            >
                              {act.label}
                            </Button>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card>
        )}

        {/* Cancel Confirmation Modal */}
        <Modal show={cancelModal.show} onHide={() => setCancelModal({ show: false, production: null })} centered>
          <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
            <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700, color: '#991b1b' }}>
              <XCircleIcon style={{ width: '18px', height: '18px', marginRight: '8px', color: '#dc2626' }} />
              Cancel Production
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: '0.88rem', color: '#374151' }}>
              Are you sure you want to cancel <strong>{cancelModal.production?.name || `Production #${cancelModal.production?.id}`}</strong>?
            </p>
            {cancelModal.production?.inventory_deducted && cancelModal.production.ingredients.filter(i => i.ingredient !== null).length > 0 && (
              <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '0.82rem' }}>
                <div style={{ fontWeight: 700, color: '#166534', marginBottom: '8px' }}>The following will be returned to inventory:</div>
                {cancelModal.production.ingredients.filter(i => i.ingredient !== null).map((ing, i) => (
                  <div key={i} style={{ color: '#166534' }}>{ing.ingredient_name} — {fmtQty(ing.quantity, ing.unit)}</div>
                ))}
              </div>
            )}
            {cancelModal.production && !cancelModal.production.inventory_deducted && (
              <div style={{ backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '10px', fontSize: '0.82rem', color: '#854d0e' }}>
                Inventory was not deducted for this production — nothing will be returned.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
            <Button variant="outline-secondary" size="sm" onClick={() => setCancelModal({ show: false, production: null })}>
              Keep Production
            </Button>
            <Button variant="danger" size="sm" onClick={() => {
              if (cancelModal.production) updateStatus(cancelModal.production.id, 'cancelled');
              setCancelModal({ show: false, production: null });
            }}>
              Confirm Cancel
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </CompanyLayout>
  );
}
